import { useEffect, useState } from "react";
import { Download, HandCoins } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, rupiah } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { FibiLoading } from "../components/FibiLoading";

const BULAN_ID = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const fmtMo = (ym) => { const [y, m] = (ym || "").split("-"); return `${BULAN_ID[Number(m) - 1] || ""} ${y || ""}`; };

export default function PenghasilanAgen() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [upline, setUpline] = useState("all");
  const [uplines, setUplines] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    api.get("/agents").then((r) => {
      const ags = r.data || [];
      const codes = [...new Set(ags.filter((a) => a.upline_code).map((a) => a.upline_code))];
      setUplines(codes.map((c) => ({ code: c, name: (ags.find((a) => a.code === c) || {}).name || "" }))
        .sort((a, b) => a.code.localeCompare(b.code)));
    }).catch(() => {});
  }, []);

  const load = () => {
    setLoading(true);
    api.get(`/reports/penghasilan-agen?month=${month}${upline !== "all" ? `&upline=${upline}` : ""}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [month, upline]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = data?.rows || [];
  const t = data?.totals;
  const periodLabel = fmtMo(data?.month || month);

  const downloadPdf = async () => {
    if (!data) return;
    setPdfBusy(true);
    try {
      const { jsPDF } = await import("jspdf");
      const { default: autoTable } = await import("jspdf-autotable");
      const doc = new jsPDF({ orientation: "landscape" });
      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.text("FASNET FIBER — Laporan Penghasilan Agen", 14, 15);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(`Periode: ${periodLabel}${upline !== "all" ? ` • Upline: ${upline}` : ""} • Digenerate: ${new Date().toLocaleString("id-ID")}`, 14, 21);
      autoTable(doc, {
        startY: 26,
        head: [["Kode", "Nama Agen", "Tipe", "Upline", "Trx", "Omset (Harga Jual)", "Harga Modal", "Margin", "Komisi", "Penghasilan"]],
        body: [
          ...rows.map((r) => [r.code, r.name, r.type, r.upline || "-", String(r.trx), rupiah(r.omset), rupiah(r.modal), rupiah(r.margin), rupiah(r.komisi), rupiah(r.penghasilan)]),
          ["", "TOTAL", "", "", String(t.trx), rupiah(t.omset), rupiah(t.modal), rupiah(t.margin), rupiah(t.komisi), rupiah(t.penghasilan)],
        ],
        styles: { fontSize: 8 },
        headStyles: { fillColor: [240, 78, 35] },
        columnStyles: { 4: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right" }, 7: { halign: "right" }, 8: { halign: "right" }, 9: { halign: "right" } },
      });
      doc.save(`penghasilan-agen-${data.month}.pdf`);
      toast.success("PDF laporan diunduh");
    } catch { toast.error("Gagal membuat PDF"); } finally { setPdfBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="penghasilan-agen-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><HandCoins className="w-7 h-7 text-[#F04E23]" /> Penghasilan Agen</h1>
          <p className="text-sm text-muted-foreground mt-1">Per agen per bulan: Penghasilan = (Harga Jual − Harga Modal) + Komisi.</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div className="space-y-1">
            <Label className="text-[11px]">Upline / Teknisi Kepala</Label>
            <Select value={upline} onValueChange={setUpline}>
              <SelectTrigger className="w-52 h-9" data-testid="select-pa-upline"><SelectValue placeholder="Semua" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="pa-upline-all">Semua Agen</SelectItem>
                {uplines.map((u) => <SelectItem key={u.code} value={u.code} data-testid={`pa-upline-${u.code}`}>{u.code} — {u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[11px]">Bulan</Label>
            <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="w-40 h-9" data-testid="input-pa-month" />
          </div>
          <Button variant="outline" className="h-9" onClick={downloadPdf} disabled={pdfBusy || !rows.length} data-testid="btn-pa-pdf"><Download className="w-4 h-4 mr-1.5" /> {pdfBusy ? "Membuat..." : "Unduh PDF"}</Button>
        </div>
      </div>

      <Card className="bg-card border-border shadow-sm overflow-x-auto" data-testid="pa-table-card">
        <CardHeader><CardTitle className="text-base font-heading">Rincian per Agen — {periodLabel}</CardTitle></CardHeader>
        <Table className="min-w-[980px]" data-testid="pa-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">#</TableHead>
              <TableHead>Kode</TableHead>
              <TableHead>Nama Agen</TableHead>
              <TableHead>Tipe</TableHead>
              <TableHead>Upline</TableHead>
              <TableHead className="text-right">Trx</TableHead>
              <TableHead className="text-right">Omset (Harga Jual)</TableHead>
              <TableHead className="text-right">Harga Modal</TableHead>
              <TableHead className="text-right">Margin</TableHead>
              <TableHead className="text-right">Komisi</TableHead>
              <TableHead className="text-right">Penghasilan</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={11} className="p-0"><FibiLoading text="FIBI sedang memuat data..." size={20} /></TableCell></TableRow>}
            {!loading && !rows.length && <TableRow><TableCell colSpan={11} className="text-center py-8 text-muted-foreground" data-testid="pa-empty">Belum ada data untuk bulan ini</TableCell></TableRow>}
            {!loading && rows.map((r, i) => (
              <TableRow key={r.code} data-testid={`pa-row-${r.code}`}>
                <TableCell className="text-xs text-muted-foreground">{i + 1}</TableCell>
                <TableCell className="font-mono text-xs font-bold">{r.code}</TableCell>
                <TableCell className="text-xs font-semibold">{r.name}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{r.type}</TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">{r.upline || "-"}</TableCell>
                <TableCell className="text-right font-mono text-xs">{r.trx}</TableCell>
                <TableCell className="text-right font-mono text-xs" data-testid={`pa-omset-${r.code}`}>{rupiah(r.omset)}</TableCell>
                <TableCell className="text-right font-mono text-xs text-rose-400">{r.modal ? rupiah(r.modal) : "-"}</TableCell>
                <TableCell className={`text-right font-mono text-xs ${r.margin >= 0 ? "" : "text-rose-400"}`}>{rupiah(r.margin)}</TableCell>
                <TableCell className="text-right font-mono text-xs text-cyan-400" data-testid={`pa-komisi-${r.code}`}>{rupiah(r.komisi)}</TableCell>
                <TableCell className={`text-right font-mono text-xs font-bold ${r.penghasilan >= 0 ? "text-emerald-400" : "text-rose-400"}`} data-testid={`pa-penghasilan-${r.code}`}>{rupiah(r.penghasilan)}</TableCell>
              </TableRow>
            ))}
            {!loading && rows.length > 0 && t && (
              <TableRow className="bg-accent/40 font-bold" data-testid="pa-row-total">
                <TableCell colSpan={5} className="text-xs font-bold">TOTAL ({rows.length} agen)</TableCell>
                <TableCell className="text-right font-mono text-xs font-bold">{t.trx}</TableCell>
                <TableCell className="text-right font-mono text-xs font-bold">{rupiah(t.omset)}</TableCell>
                <TableCell className="text-right font-mono text-xs font-bold text-rose-400">{rupiah(t.modal)}</TableCell>
                <TableCell className="text-right font-mono text-xs font-bold">{rupiah(t.margin)}</TableCell>
                <TableCell className="text-right font-mono text-xs font-bold text-cyan-400">{rupiah(t.komisi)}</TableCell>
                <TableCell className="text-right font-mono text-sm font-bold text-emerald-400" data-testid="pa-total-penghasilan">{rupiah(t.penghasilan)}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
      <p className="text-[11px] text-muted-foreground" data-testid="pa-note">
        Omset dari transaksi yang dicatat atas nama agen (sold_by) berstatus lunas. Harga modal voucher diambil dari data voucher, produk GSM/alat dari harga modal produk. Komisi dari komisi tercatat pada seluruh transaksi bulan ini. Penghasilan = (Omset − Modal) + Komisi.
      </p>
    </div>
  );
}
